import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useRef, useState } from 'react';

import Button from '@/components/common/Button/Button';
import LoadingPage from '@/components/common/LoadingPage/LoadingPage';
import DayTabs from '@/components/goalbased/MealsPlan/DaysTabs/DaysTabs';
import Header from '@/components/goalbased/MealsPlan/Header/Header';
import { useMediaQuery } from '@/hooks';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import { useScrollYPosition } from '@/hooks/useScrollYPosition';
import type { AttributeModelType } from '@/models/api';
import type { OrderPlan } from '@/models/cart/order-plan';
import { useStore } from '@/models/root-store';
import { API_GOAL_MAP } from '@/utils/api-constants';
import { LoadingState, Mode } from '@/utils/constants';
import { SetCategoryOnScroll } from '@/utils/helpers';
import { trackMealPlanAddedToCart } from '@/utils/track/tracker.helper';

import { BackModal } from '../BackModal/BackModal';
import Row from './Row/Row';

const renderText = (mode: Mode) => {
  switch (mode) {
    case Mode.EDIT_CART:
      return 'Update Cart';
    case Mode.EDIT_SUBSCRIPTION:
      return 'Update Subscription';
    default:
      return 'Add to Cart';
  }
};

type Props = {
  setIsLoading: (val: boolean) => void;
  selectedPlan: OrderPlan;
};

const MealsPlan: React.FC<Props> = observer(
  ({ setIsLoading, selectedPlan }: Props) => {
    const { cartStore, productStore } = useStore();
    const { draftCart, goalBased } = cartStore;
    const { activeDay, setActiveDay, setSwapCategory } = goalBased;
    const { productState, productsFullList } = productStore;
    const config = selectedPlan.attributes.find(
      (attr: AttributeModelType) => attr.name === 'config',
    )?.value;
    const productCollection = selectedPlan.attributes.find(
      (attr: AttributeModelType) => attr.name === 'productCollection',
    )?.value;
    const swapConfig = selectedPlan.attributes.find(
      (attr: AttributeModelType) => attr.name === 'swapConfig',
    )?.value;
    const nod = selectedPlan.numberOfDays;
    const days = Array.from(Array(nod).keys()).map((i) => i + 1);
    const router = useRouter();

    const categoryRefs = useRef<(HTMLDivElement | null)[]>(
      new Array(days.length).fill(null),
    );

    const headerRef: any = useRef(null);
    const stickyRef: any = useRef(null);
    const daysRef: any = useRef(null);
    const [showBackModal, setShowBackModal] = useState(false);
    const stickyRefHeight = stickyRef.current?.clientHeight || 176;

    const handleCartClick = async () => {
      setIsLoading(true);
      const { mode } = draftCart;
      switch (mode) {
        case Mode.EDIT_CART:
          try {
            trackMealPlanAddedToCart(draftCart, true, productsFullList);
          } catch (error) {
            // ignore error
          }
          cartStore.copyDraftCartToCart();
          await router.push({
            pathname: '/',
            query: { cartOpen: true },
          });
          cartStore.resetDraftCart();
          goalBased.reset();
          break;
        case Mode.EDIT_SUBSCRIPTION:
          // direct back to my account and open cart
          await router.push({
            pathname: '/account',
            query: { mode, cartOpen: true },
          });
          goalBased.reset();
          draftCart.setPlanIdToView('');
          break;
        default:
          try {
            trackMealPlanAddedToCart(draftCart, false, productsFullList);
          } catch (error) {
            // ignore error
          }
          cartStore.copyDraftCartToCart();

          await router.push({
            pathname: '/',
            query: { cartOpen: true },
          });
          cartStore.resetDraftCart();
          goalBased.reset();
      }
    };

    const openModal = () => {
      setShowBackModal(true);
    };

    const onContinue = () => {
      setShowBackModal(false);
      cartStore.resetDraftCart();
      goalBased.resetPackDetails();
      delete router.query.planId;
      router.replace({
        query: { ...router.query },
      });
    };

    const closeModal = () => {
      setShowBackModal(false);
    };

    const isMobileDevice = useMediaQuery(`(max-width: 767px)`);
    const setDayOnScroll = (data: any) => {
      if (!isMobileDevice) {
        setActiveDay(Number(data));
      }
    };
    useEffect(() => {
      if (activeDay > days.length) {
        setActiveDay(1);
      }
    }, [days.length]);

    SetCategoryOnScroll(categoryRefs, setDayOnScroll, stickyRefHeight);

    const onScrollToDay = async (id: any) => {
      await setActiveDay(id);
      if (!isMobileDevice) {
        categoryRefs?.current[id]?.scrollIntoView();
      }
    };

    useEffect(() => {
      // update the preset for the plan
      if (selectedPlan?.planId) {
        const goal = selectedPlan.findAttribute('goal');
        if (goal) {
          const goalKey = _.findKey(API_GOAL_MAP, (v) => v === goal);
          goalBased.setPreset('goal', goalKey);
        }
      }
    }, [selectedPlan?.planId]);

    useEffect(() => {
      const { scrollPosition, routerPathname } =
        selectedPlan.selectedItemPosition;
      // we will need to set the product collection to goal name and the swap config
      if (productCollection) {
        productStore.setProductCollection(productCollection);
      }

      if (swapConfig) {
        productStore.setSwapConfig(swapConfig);
      }

      if (scrollPosition && routerPathname === router.pathname) {
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
          selectedPlan.setScrollPosition(0);
        }, 10);
      } else {
        window.scrollTo(0, 0);
      }
    }, []);

    useEffect(() => {
      if (window.location.hash) {
        const dayNumber = parseInt(window.location.hash.substr(5, 1), 10);
        setTimeout(() => {
          setActiveDay(dayNumber);
        }, 100);
      }
    }, [window.location.hash]);

    const scrollYPosition = useScrollYPosition();
    const scrollDirection = useScrollDirection();
    const direction = scrollDirection !== 'up';
    const isDaysScrolling =
      scrollYPosition >= (daysRef.current && daysRef.current.offsetTop);
    const daysPosition =
      daysRef.current?.offsetTop !== undefined ? isDaysScrolling : false;
    const isHeaderScrolling =
      scrollYPosition >= (headerRef.current && headerRef.current.offsetTop)
        ? direction
        : false;
    const headerPosition =
      headerRef.current?.offsetTop !== undefined ? isHeaderScrolling : false;

    if (productState !== LoadingState.DONE) {
      return <LoadingPage />;
    }

    return (
      <div>
        <div
          className={`${
            headerPosition || daysPosition ? 'hidden md:block' : 'hidden'
          } fixed top-0 z-50 w-full bg-white shadow-bottom`}
          ref={stickyRef}
        >
          <div className="container px-0">
            <div className={'hidden md:block'}>
              <Header position="fixed" orderPlan={selectedPlan} />
            </div>
            <div className={`hidden px-3 pb-4 pt-1.5 md:block lg:px-8 lg:pt-0`}>
              <DayTabs
                days={days}
                onScrollToDay={onScrollToDay}
                active={activeDay}
              />
            </div>
          </div>
        </div>
        <div className="container px-4 py-8">
          {!draftCart.mode && (
            <div className="mb-3 w-fit">
              <button
                className="flex cursor-pointer items-center space-x-3 outline-none"
                onClick={openModal}
              >
                <div className="flex h-10 w-20 cursor-pointer items-center rounded-32 bg-white py-2">
                  <Image
                    width="12"
                    height="12"
                    src="/assets/images/png/arrow-back.png"
                    alt={''}
                    className="object-contain"
                  />
                  <span className="pl-4 font-interMedium text-base text-black">
                    Back
                  </span>
                </div>
              </button>
            </div>
          )}

          <div ref={headerRef}>
            <Header orderPlan={selectedPlan} />
          </div>
          <div className="my-6">
            <div ref={daysRef}>
              <DayTabs
                days={days}
                onScrollToDay={onScrollToDay}
                active={activeDay}
              />
            </div>
            {days.map((day, index) => (
              <div
                key={index}
                id={(index + 1).toString()}
                ref={(el: any) => {
                  categoryRefs.current[index + 1] = el;
                }}
                style={{ scrollMargin: stickyRefHeight - 30 }}
              >
                <Row
                  key={index}
                  active={activeDay}
                  config={config}
                  day={day}
                  items={selectedPlan.getItemsByDay(day)}
                  orderPlan={selectedPlan}
                  draftCart={draftCart}
                  setSwapCategory={setSwapCategory}
                  swapConfig={goalBased.selectedSwapConfig}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="fixed bottom-0 z-10 flex w-full items-center justify-center	bg-white px-3 py-6 shadow-3xl md:justify-center">
          <Button
            className="w-80 py-[5px] md:w-[380px] md:py-3"
            onClick={handleCartClick}
            theme="primary"
          >
            <div className="flex w-full items-center justify-between px-4">
              <span className="font-interSemibold">
                {renderText(draftCart.mode)}
              </span>
              <span className="font-interSemibold text-base leading-[30px] md:font-interMedium md:text-2xl">
                ${selectedPlan.total.toFixed(2)}
              </span>
            </div>
          </Button>
        </div>
        <BackModal
          isOpen={showBackModal}
          onClose={closeModal}
          onContinue={onContinue}
        />
      </div>
    );
  },
);

export default memo(MealsPlan);
